var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import * as d3 from 'd3';
import { colorPalette as defaultColorPalette, defaultMargins } from '../utils';
import { Canvas } from '../components/Canvas';
import { TimelineGroup } from './components/TimelineGroup';
import { scaleTime } from '@visx/scale';
import { useCharts } from '../components/Charts';
import { TrackArea } from '../TrackArea';
import { Legend } from '../components/Legend';
import { Axis } from '../Axis/Axis';
import { getSeriesBy } from '../utils/getSeriesBy';
import { IntervalGuides } from '../components/IntervalGuides';
export function Timelines(_a) {
    var name = _a.name, series = _a.series, accessors = _a.accessors, xDomain = _a.xDomain, defaultActive = _a.defaultActive, marginProps = _a.margins, _b = _a.colorPalette, colorPalette = _b === void 0 ? defaultColorPalette : _b, _c = _a.barHeight, barHeight = _c === void 0 ? 24 : _c, _d = _a.verticalSpacing, verticalSpacing = _d === void 0 ? 16 : _d, className = _a.className, _e = _a.axesProps, axesProps = _e === void 0 ? {} : _e, tooltip = _a.tooltip, meta = _a.meta;
    var width = useCharts().width;
    var _f = React.useState(undefined), hovered = _f[0], setHovered = _f[1];
    var _g = React.useState(new Set(defaultActive !== null && defaultActive !== void 0 ? defaultActive : Object.values(series).map(function (d) { return accessors.key(d); }))), activeSeries = _g[0], setActiveSeries = _g[1];
    var valid = React.useMemo(function () {
        var _a;
        if (accessors.valid !== undefined) {
            return (_a = getSeriesBy(series, accessors.valid, 'valid')) !== null && _a !== void 0 ? _a : {};
        }
        return series;
    }, [series]);
    var active = React.useMemo(function () {
        return valid !== undefined
            ? Object.values(valid).filter(function (s) { return activeSeries.has(accessors.key(s)); })
            : [];
    }, [valid, activeSeries]);
    // Decorate `margins` with default values of zero for unspecified properties
    var margins = React.useMemo(function () { return defaultMargins(marginProps); }, [
        marginProps
    ]);
    // Calculate canvas `height` and `barSize` based on number of series + spacing
    var _h = React.useMemo(function () {
        var barSize = barHeight + verticalSpacing;
        var height = barSize * Object.values(valid).length + margins.top + margins.bottom;
        return {
            height: height,
            barSize: barSize
        };
    }, [barHeight, verticalSpacing, series]), height = _h.height, barSize = _h.barSize;
    var xScale = React.useMemo(function () {
        return scaleTime({
            domain: xDomain,
            range: [0, width - margins.left - margins.right]
        });
    }, [xDomain, width, margins]);
    var colorScheme = React.useMemo(function () {
        return d3
            .scaleOrdinal()
            .domain(Object.values(series).map(accessors.key))
            .range(colorPalette);
    }, [series, colorPalette]);
    var clearHovered = React.useCallback(function () {
        setHovered(undefined);
    }, []);
    var handleHover = React.useCallback(function (id) {
        if (activeSeries.has(id)) {
            setHovered(id);
        }
    }, [activeSeries]);
    // Prevent hover effect from persisting after user has
    // deactivated a series (i.e., clicked on a legend chip)
    var handleLegendClick = React.useCallback(function (active, id) {
        if (id !== undefined && active.has(id)) {
            setHovered(id);
        }
        else {
            setHovered(undefined);
        }
        setActiveSeries(active);
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(Canvas, { height: height, margins: margins },
            React.createElement(IntervalGuides, { xScale: xScale }),
            React.createElement(TimelineGroup, { activeSet: activeSeries, hovered: hovered, series: series, accessors: accessors, colorScheme: colorScheme, xDomain: xDomain, barHeight: barSize, verticalSpacing: verticalSpacing, className: className }),
            React.createElement(TrackArea, { pinId: name, series: active, accessors: accessors, height: height, xScale: xScale, meta: meta, tooltip: tooltip, colorScheme: colorScheme }),
            React.createElement(Axis, __assign({}, axesProps.bottom, { height: 40, margins: __assign(__assign({}, margins), { top: 0, bottom: 0 }), orientation: "bottom", scale: xScale, isCanvasChild: true }))),
        React.createElement(Legend, { activeSet: activeSeries, series: series, accessors: accessors, colorScheme: colorScheme, onToggle: handleLegendClick, onMouseEnter: handleHover, onMouseLeave: clearHovered, marginLeft: margins === null || margins === void 0 ? void 0 : margins.left, marginRight: margins === null || margins === void 0 ? void 0 : margins.right })));
}
//# sourceMappingURL=Timelines.js.map